import { useTranslation } from "react-i18next";
import {
  BeakerIcon,
  ChartBarIcon,
  MapIcon,
  WalletIcon,
} from "@heroicons/react/24/outline";

import StatCard from "@components/zenith/ui/StatCard";
import { useSubscriberLevel0Resources } from "@hooks/query/zenith/useSubscriberLevel0Resources";
import { formatVolume } from "@utils/formatVolume";
import { sumVolumes, zoneVolumes } from "@utils/volumes";
import { useDashboardPeriod } from "./useDashboardPeriod";

const SummaryWidget: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const { isPeriodLoading, isPeriodFetching, periodNotice } =
    useDashboardPeriod();
  const { data, isLoading, isFetching, isError } =
    useSubscriberLevel0Resources();

  if (isLoading || isPeriodLoading) return null;

  // A failed refresh keeps the zones last loaded, so only zones that never loaded are unavailable.
  const unavailable = isError && !data;
  const zones = data ?? [];
  const { remaining, used, allocated } = sumVolumes(zones.map(zoneVolumes));
  // Each figure belongs to an accounting period.
  const figure = (litres: number) =>
    unavailable
      ? t("zenith.dashboard.unavailable")
      : (periodNotice ?? formatVolume(litres));

  return (
    <section
      aria-label={t("common.summary") as string}
      aria-busy={isFetching || isPeriodFetching}
      // Columns follow the content width, which the info panel narrows.
      className="grid grid-cols-fit-40 gap-4"
    >
      <StatCard
        label={t("zenith.dashboard.remaining")}
        value={figure(remaining)}
        sub={t("zenith.dashboard.remaining_sub")}
        icon={BeakerIcon}
      />
      <StatCard
        label={t("zenith.dashboard.allocated")}
        value={figure(allocated)}
        sub={t("zenith.dashboard.allocated_sub")}
        icon={WalletIcon}
        tone="info"
      />
      <StatCard
        label={t("zenith.dashboard.used")}
        value={figure(used)}
        sub={
          !periodNotice && !unavailable
            ? t("zenith.dashboard.used_sub", {
                pct:
                  allocated > 0 ? ((used / allocated) * 100).toFixed(1) : "-",
              })
            : null
        }
        icon={ChartBarIcon}
        tone="warning"
      />
      <StatCard
        label={t("zenith.dashboard.active_level0wrs")}
        value={unavailable ? t("zenith.dashboard.unavailable") : zones.length}
        icon={MapIcon}
        tone="success"
      />
    </section>
  );
};

export default SummaryWidget;
